import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Divider, Button, Chip, Card, CardContent, FormControlLabel, Switch } from '@mui/material';
import { CheckCircle, Warning, School, SentimentDissatisfied, Schedule, PauseCircleOutline } from '@mui/icons-material';
import apiClient from '../../config/axios';
import { useAuth } from '../../contexts/AuthContext';
import ModalResult from '../../components/ModalResult';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import ModalConfirm from '../../components/ModalConfirm';
import { useNavigate } from 'react-router-dom';
import CourseFilesMember from '../../components/CourseFilesMember';
import getConfig from '../../config/app-config';

const MyCourses = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const config = getConfig();

  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState('success'); // 'success' | 'error'
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [enrollmentToCancel, setEnrollmentToCancel] = useState(null);

  // Stati per le impostazioni di visualizzazione
  const [hideExpiredCourses, setHideExpiredCourses] = useState(false);

  // Carica le impostazioni dal localStorage all'avvio
  useEffect(() => {
    const savedHideExpired = localStorage.getItem('hideExpiredCourses');

    if (savedHideExpired !== null) {
      setHideExpiredCourses(JSON.parse(savedHideExpired));
    }
  }, []);

  // Salva le impostazioni nel localStorage quando cambiano
  useEffect(() => {
    localStorage.setItem('hideExpiredCourses', JSON.stringify(hideExpiredCourses));
  }, [hideExpiredCourses]);

  // Filtra gli enrollments in base alle impostazioni
  const filteredEnrollments =
    user?.enrollments
      ?.filter((enrollment) => {
        if (hideExpiredCourses && enrollment.course.endDate && dayjs(enrollment.course.endDate).isBefore(dayjs(), 'day')) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        // Ordina: prima corsi senza minLevel, poi per livello crescente
        const levelA = a.course.minLevel || 0;
        const levelB = b.course.minLevel || 0;

        // Se uno ha minLevel e l'altro no, quello senza minLevel va prima
        if (levelA === 0 && levelB !== 0) return -1;
        if (levelA !== 0 && levelB === 0) return 1;

        // Se entrambi hanno minLevel, ordina per livello crescente
        return levelA - levelB;
      }) || [];

  // Controlla se un corso è scaduto
  const isCourseExpired = (endDate) => {
    return endDate && dayjs(endDate).isBefore(dayjs(), 'day');
  };

  // Aggiorna enrollments se cambia user.enrollments
  // (non serve più lo stato enrollments)

  // const handleRequestCancelEnrollment = (enrollmentId) => {
  //   setEnrollmentToCancel(enrollmentId);
  //   setConfirmOpen(true);
  // };

  const handleConfirmCancelEnrollment = async () => {
    if (!enrollmentToCancel) return;
    try {
      await apiClient.delete(`/api/enrollments/${enrollmentToCancel}`);
      setSuccess('Iscrizione cancellata con successo!');
      setModalType('success');
      setModalOpen(true);
      updateUser({ ...user, enrollments: user.enrollments.filter((enrollment) => enrollment._id !== enrollmentToCancel) });
    } catch (error) {
      setError("Errore durante la cancellazione dell'iscrizione");
      setModalType('error');
      setModalOpen(true);
    } finally {
      setConfirmOpen(false);
      setEnrollmentToCancel(null);
    }
  };

  const handleCancelModalConfirm = () => {
    setConfirmOpen(false);
    setEnrollmentToCancel(null);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSuccess(null);
    setError(null);
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      monthly: 'Mensile',
      annual3: 'Annuale (3 rate)',
      annual1: 'Annuale (1 rata)',
    };
    return methods[method] || method;
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      case 'overdue':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPaymentStatusLabel = (status) => {
    switch (status) {
      case 'paid':
        return 'Pagato';
      case 'pending':
        return 'In Attesa';
      case 'failed':
        return 'Fallito';
      case 'overdue':
        return 'Da Pagare'; // Modificato da 'Scaduto' a 'Da Pagare'
      default:
        return status;
    }
  };

  if (!user) {
    return (
      <Container maxWidth='lg'>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Typography>Caricamento...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth='md'>
      <Box sx={{ py: 3 }}>
        {/* Header compatto */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            // background: 'linear-gradient(90deg, #e3f2fd 0%, #fff 100%)',
          }}
        >
          <School color='primary' sx={{ fontSize: 32 }} />
          <Typography variant='h5' fontWeight={700} color='primary.main'>
            Le Mie Iscrizioni
          </Typography>
        </Box>

        {/* Opzioni di visualizzazione */}
        <Card sx={{ mb: 3, p: 2, boxShadow: 1 }}>
          <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1.5, color: 'text.secondary' }}>
            Opzioni di Visualizzazione
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={hideExpiredCourses}
                  onChange={(e) => setHideExpiredCourses(e.target.checked)}
                  size='small'
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#4caf50', // Verde quando è attivo
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#4caf50', // Track verde quando è attivo
                      },
                    },
                  }}
                />
              }
              label='Nascondi corsi terminati'
              sx={{ fontSize: 14 }}
            />
          </Box>
        </Card>

        {/* Modal per risultati operazioni */}
        <ModalResult open={modalOpen} onClose={handleCloseModal} message={success || error} severity={modalType} />

        {/* Modal di conferma cancellazione iscrizione */}
        <ModalConfirm open={confirmOpen} title='Conferma Cancellazione' message="Sei sicuro di voler cancellare questa iscrizione? L'operazione non è reversibile." onConfirm={handleConfirmCancelEnrollment} onCancel={handleCancelModalConfirm} />

        {!filteredEnrollments || filteredEnrollments.length === 0 ? (
          <Card sx={{ mt: 4, p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center', boxShadow: 2 }}>
            <SentimentDissatisfied color='disabled' sx={{ fontSize: 40, mb: 1 }} />
            <Typography variant='subtitle1' color='text.secondary' align='center'>
              {!user.enrollments || user.enrollments.length === 0 ? 'Non hai ancora iscrizioni ai corsi' : 'Nessuna iscrizione visibile con i filtri attuali'}
            </Typography>
            <Typography variant='body2' color='text.secondary' align='center' sx={{ mt: 0.5 }}>
              {!user.enrollments || user.enrollments.length === 0 ? 'Vai alla sezione Corsi per iscriverti ai corsi disponibili' : 'Prova a modificare le opzioni di visualizzazione per vedere più iscrizioni'}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button variant='contained' size='small' onClick={() => navigate('/courses')}>
                Vai ai Corsi
              </Button>
              {/* <MembershipActivationButton 
                variant="outlined" 
                size="small"
                // autoFetch={false}
              /> */}
            </Box>
          </Card>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filteredEnrollments.map((enrollment) => {
              const isSuspended = enrollment.status === 'suspended';
              const paymentInfo = enrollment.paymentStatusInfo || {};
              
              return (
                <Card key={enrollment._id} sx={{ boxShadow: 2, borderRadius: 3, p: { xs: 1, sm: 2.5 }, position: 'relative', border: '1px solid #e0e0e0' }}>
                  <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1.5 }}>
                      <Typography variant='subtitle1' fontWeight={700} sx={{ color: 'primary.main', fontSize: 20, letterSpacing: 0.2 }}>
                        {enrollment.course.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {isSuspended && <Chip label='Iscrizione sospesa' color='warning' size='small' icon={<PauseCircleOutline sx={{ fontSize: 18 }} />} sx={{ fontSize: 12, height: 24, fontWeight: 600 }} />}
                        {isCourseExpired(enrollment.course.endDate) && <Chip label='Corso Scaduto' color='warning' size='small' icon={<Schedule sx={{ fontSize: 18 }} />} sx={{ fontSize: 12, height: 24, fontWeight: 600 }} />}
                        <Chip label={getPaymentStatusLabel(paymentInfo.isEnrollmentPaid ? 'paid' : 'overdue')} color={getPaymentStatusColor(paymentInfo.isEnrollmentPaid ? 'paid' : 'overdue')} size='small' icon={paymentInfo.isEnrollmentPaid ? <CheckCircle sx={{ fontSize: 18 }} /> : <Warning sx={{ fontSize: 18 }} />} sx={{ fontSize: 14, height: 28, fontWeight: 600, letterSpacing: 0.2 }} />
                      </Box>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        gap: 2,
                        mb: 1.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant='body2' color='text.secondary' sx={{ fontSize: 14 }}>
                          <b>Data Iscrizione:</b> {dayjs(enrollment.createdAt).locale('it').format('DD MMM YY')}
                        </Typography>
                        {enrollment.startDate && (
                          <Typography variant='body2' color='text.secondary' sx={{ fontSize: 14 }}>
                            <b>Iscritto a partire da:</b> {dayjs(enrollment.startDate).locale('it').format('MMMM YYYY')}
                          </Typography>
                        )}
                        <Typography variant='body2' color='text.secondary' sx={{ fontSize: 14 }}>
                          <b>Docente:</b> {enrollment.course.teacher ? `${enrollment.course.teacher.name} ${enrollment.course.teacher.surname}` : 'Da assegnare'}
                        </Typography>

                        <Typography variant='body2' color='text.secondary' sx={{ fontSize: 14 }}>
                          <b>Anno di corso:</b> {enrollment.course.minLevel ? (enrollment.course.minLevel >= 6 ? 'Oltre il 5° anno' : `${enrollment.course.minLevel}°`) : 'Libero'}
                        </Typography>

                        {enrollment.course.startDate && (
                          <Typography variant='body2' color='text.secondary' sx={{ fontSize: 14 }}>
                            <b>Data Inizio Corso:</b> {dayjs(enrollment.course.startDate).locale('it').format('DD MMM YY')}
                          </Typography>
                        )}
                        {enrollment.course.endDate && (
                          <Typography variant='body2' color='text.secondary' sx={{ fontSize: 14 }}>
                            <b>Data Fine Corso:</b> {dayjs(enrollment.course.endDate).locale('it').format('DD MMM YY')}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography variant='body2' color='text.secondary' sx={{ fontSize: 14 }}>
                          <b>Cadenza di Pagamento:</b> {getPaymentMethodLabel(enrollment.paymentMethod)}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' sx={{ fontSize: 14 }}>
                          <b>Importo:</b> €{enrollment.amount}
                        </Typography>
                        <Typography variant='body2' color='text.secondary' sx={{ fontSize: 14 }}>
                          <b>Prossimo Pagamento:</b> {paymentInfo.nextPaymentDate ? dayjs(paymentInfo.nextPaymentDate).locale('it').format('DD MMM YY') : '--'}
                        </Typography>
                        {paymentInfo.paymentsMade !== undefined && enrollment.totalPayments !== undefined && (
                          <Typography variant='body2' color='text.secondary' sx={{ fontSize: 14 }}>
                            <b>Pagamenti:</b> {paymentInfo.paymentsMade}/{enrollment.totalPayments}
                          </Typography>
                        )}
                        <Typography variant='body2' color={paymentInfo.paymentsLate > 0 ? 'error' : 'text.secondary'} sx={{ fontSize: 14 }}>
                          <b>Pagamenti in Ritardo:</b> {paymentInfo.paymentsLate ?? 0}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Materiali del Corso */}
                    {config.WITH_COURSE_MATERIALS && <CourseFilesMember courseId={enrollment.course._id} />}
                    <Divider sx={{ mb: 1 }} />
                    {!enrollment.paymentStatusInfo.isEnrollmentPaid && (
                      <Typography variant='body2' textAlign='right' color='mediumblue' sx={{ fontSize: 14 }}>
                        Puoi pagare online ora o direttamente in segreteria
                      </Typography>
                    )}
                    {enrollment.paymentStatusInfo.isEnrollmentPaid && enrollment.paymentMethod === 'annual3' && paymentInfo.paymentsMade !== enrollment.totalPayments && (
                      <Typography variant='body2' textAlign='right' color='mediumblue' sx={{ fontSize: 14 }}>
                      I pagamenti annuali a 3 rate possono essere pagati anche in anticipo
                      </Typography>
                    )}
                    {/* Consentiamo di pagare solo se non pagato o se il metodo di pagamento è annuale a 3 rate */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1, gap: 1 }}>
                      {(!enrollment.paymentStatusInfo.isEnrollmentPaid || (enrollment.paymentMethod === 'annual3' && paymentInfo.paymentsMade !== enrollment.totalPayments)) && (
                        <Button size='small' color='primary' variant='contained' onClick={() => navigate('/course-upkeep-checkout', { state: { enrollment } })} sx={{ fontWeight: 700, borderRadius: 2, minWidth: 0, px: 2, py: 0.7, fontSize: 14, boxShadow: 0 }}>
                          Paga Ora
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default MyCourses;
